import { create } from 'zustand';
import { useDocumentStore } from '@/store/documentStore';

export interface SearchState {
  /** Current search query string. */
  query: string;
  /** Node IDs that match the current query. */
  matchingNodeIds: string[];
  /** Index into matchingNodeIds for the active match. */
  currentMatchIndex: number;
  /** Whether the search bar is open. */
  isOpen: boolean;

  // Actions
  openSearch: () => void;
  closeSearch: () => void;
  setQuery: (query: string) => void;
  nextMatch: () => void;
  prevMatch: () => void;
}

export const useSearchStore = create<SearchState>((set, get) => ({
  query: '',
  matchingNodeIds: [],
  currentMatchIndex: 0,
  isOpen: false,

  openSearch: () => set({ isOpen: true }),

  closeSearch: () =>
    set({
      isOpen: false,
      query: '',
      matchingNodeIds: [],
      currentMatchIndex: 0,
    }),

  setQuery: (query: string) => {
    if (!query.trim()) {
      set({ query, matchingNodeIds: [], currentMatchIndex: 0 });
      return;
    }

    const { nodes } = useDocumentStore.getState();
    const lowerQuery = query.toLowerCase();
    const matching: string[] = [];

    for (const [id, node] of nodes) {
      if (node.text.toLowerCase().includes(lowerQuery)) {
        matching.push(id);
      }
    }

    const prev = get();
    // Keep currentMatchIndex in bounds, reset if matches changed
    const currentMatchIndex =
      prev.currentMatchIndex < matching.length ? prev.currentMatchIndex : 0;

    set({ query, matchingNodeIds: matching, currentMatchIndex });
  },

  nextMatch: () =>
    set((state) => {
      if (state.matchingNodeIds.length === 0) return state;
      return {
        currentMatchIndex:
          (state.currentMatchIndex + 1) % state.matchingNodeIds.length,
      };
    }),

  prevMatch: () =>
    set((state) => {
      if (state.matchingNodeIds.length === 0) return state;
      return {
        currentMatchIndex:
          (state.currentMatchIndex - 1 + state.matchingNodeIds.length) %
          state.matchingNodeIds.length,
      };
    }),
}));
